import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@emotion/react';
import { Box, Stack, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tabs, Tab, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Loader } from '../loader';
import PdfViewer from '../../utils/pdfViewer/PdfViewer';

export const ResponseContext = createContext({
  transactionId: null,
  setTransactionId: () => {}
});

const ResponseTable = ({ data = {} }) => {
  const displayData = useMemo(() => {
    if (!data || typeof data !== "object") return null;
    if (Array.isArray(data)) return { items: data };
    return data?.text || data;
  }, [data]);

  if (!displayData) return null;

  const NULL_VALUES = new Set([
    null,
    undefined,
    "null",
    "NULL",
    '"null"',
    '"NULL"',
    '""',
    "",
  ]);
  const normalizeValue = (value) => (NULL_VALUES.has(value) ? "NA" : value);

  const shouldDisplayValue = (value) => {
    const normalizedValue = normalizeValue(value);
    return (
      normalizedValue !== "NA" &&
      !(Array.isArray(normalizedValue) && normalizedValue.length === 0) &&
      !(
        typeof normalizedValue === "object" &&
        normalizedValue !== null &&
        Object.keys(normalizedValue).length === 0
      )
    );
  };

  const formatSubValue = (subK, subV) => {
    const isNullish =
      subV === null ||
      subV === undefined ||
      subV === "" ||
      subV === "null" ||
      subV === "NULL" ||
      subV === '"null"' ||
      subV === '"NULL"' ||
      subV === '""';

    if (typeof subV === "object" && subV !== null) {
      if (Object.keys(subV).length === 1 && "verified" in subV) {
        return `${subK}: ${subV.verified.toString()}`;
      }
      return `${subK}: ${JSON.stringify(subV)}`;
    }

    if (isNullish) {
      return `${subK}: NA`;
    }

    return `${subK}: ${String(subV)}`;
  };

  const renderValue = (value) => {
    const normalizedValue = normalizeValue(value);
    if (normalizedValue === "NA") return "NA";
    if (!shouldDisplayValue(normalizedValue)) return "N/A";

    if (Array.isArray(normalizedValue)) {
      if (normalizedValue.every((item) => typeof item === "object")) {
        return (
          <Box>
            {normalizedValue.map((item) => (
              <Typography
                key={
                  item?.id?.toString?.() ||
                  item?.message ||
                  JSON.stringify(item)
                }
                sx={{ mb: 0.5 }}
              >
                {item.message || JSON.stringify(item)}
              </Typography>
            ))}
          </Box>
        );
      }
      return normalizedValue.join(", ");
    }

    if (normalizedValue && typeof normalizedValue === "object") {
      return (
        <TableContainer>
          <Table
            size="small"
            sx={{
              margin: 0,
              tableLayout: "fixed",
              "& td, & th": {
                border: "1px solid #dddddd",
                padding: "8px",
                fontSize: "14px",
                textAlign: "center !important",
                width: "100%",
              },
            }}
          >
            <TableBody>
              {Object.entries(normalizedValue).map(([k, v]) => {
                if (typeof v === "object" && v !== null) {
                  if (Object.keys(v).length === 1 && "verified" in v) {
                    return (
                      <TableRow key={k}>
                        <TableCell
                          sx={{ width: "35%", textAlign: "center !important" }}
                        >
                          {k}
                        </TableCell>
                        <TableCell
                          sx={{
                            width: "70%",
                            padding: "8px !important",
                            textAlign: "center !important",
                          }}
                        >
                          {v.verified.toString()}
                        </TableCell>
                      </TableRow>
                    );
                  }

                  const allNestedAreVerified =
                    v && typeof v === "object"
                      ? Object.values(v).every(
                          (nestedValue) =>
                            nestedValue &&
                            typeof nestedValue === "object" &&
                            Object.keys(nestedValue).length === 1 &&
                            "verified" in nestedValue
                        )
                      : false;

                  if (allNestedAreVerified) {
                    return (
                      <TableRow key={k}>
                        <TableCell
                          sx={{ width: "35%", textAlign: "center !important" }}
                        >
                          {k}
                        </TableCell>
                        <TableCell
                          sx={{
                            width: "70%",
                            padding: "8px !important",
                            textAlign: "center !important",
                          }}
                        >
                          <Stack spacing={1}>
                            {Object.entries(v).map(([subK, subV]) => (
                              <Typography key={subK}>
                                {`${subK}: ${subV.verified.toString()}`}
                              </Typography>
                            ))}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return (
                    <TableRow key={k}>
                      <TableCell
                        sx={{ width: "35%", textAlign: "center !important" }}
                      >
                        {k}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "70%",
                          padding: "8px !important",
                          textAlign: "center !important",
                        }}
                      >
                        <Stack spacing={1}>
                          {Object.entries(v).map(([subK, subV]) => (
                            <Typography key={subK}>
                              {formatSubValue(subK, subV)}
                            </Typography>
                          ))}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                }
                return (
                  <TableRow key={k}>
                    <TableCell sx={{ width: "35%", padding: "0px !important" }}>
                      {k}
                    </TableCell>
                    <TableCell
                      sx={{ width: "70%", textAlign: "center !important" }}
                    >
                      {String(v)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    return String(normalizedValue);
  };

  const renderObjectRows = (obj, prefix = "", isNested = false) => {
    if (!obj || typeof obj !== "object") return null;

    return Object.entries(obj).map(([key, value]) => {
      const shouldWrapWithAccordion =
        !isNested &&
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value);
      if (shouldWrapWithAccordion) {
        return (
          <TableRow key={`${prefix}${key}`}>
            <TableCell
              colSpan={2}
              sx={{
                fontWeight: "bold",
                fontSize: "16px",
                backgroundColor: "#f1f1f1",
                textAlign: "center !important",
                border: "1px solid #dddddd",
                py: 1.5,
                padding: "0px !important",
              }}
            >
              <Accordion defaultExpanded>
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon sx={{ width: "2em", height: "2em" }} />
                  }
                  sx={{ backgroundColor: "#f1f1f1" }}
                >
                  <Typography sx={{ fontWeight: 600 }}>{key}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <Table size="small">
                    <TableBody>
                      {renderObjectRows(value, `${prefix}${key}.`, true)}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            </TableCell>
          </TableRow>
        );
      }
      if (
        Array.isArray(value) &&
        value.length > 0 &&
        typeof value[0] === "object"
      ) {
        return (
          <TableRow key={`${prefix}${key}`}>
            <TableCell
              colSpan={2}
              sx={{ padding: "0px !important", textAlign: "center !important" }}
            >
              <Accordion defaultExpanded>
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon sx={{ width: "2em", height: "2em" }} />
                  }
                  sx={{ backgroundColor: "#f1f1f1" }}
                >
                  <Typography sx={{ fontWeight: 600 }}>{key}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  {value.map((item, index) => (
                    <Box key={`${prefix}${key}.${index}`} sx={{ mb: 2 }}>
                      {value?.length > 1 ? (
                        <Typography
                          sx={{
                            px: 2,
                            py: 1,
                            fontWeight: "bold",
                            backgroundColor: "#f9f9f9",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          Item {index + 1}
                        </Typography>
                      ) : (
                        <></>
                      )}
                      <Table size="small">
                        <TableBody>
                          {renderObjectRows(
                            item,
                            `${prefix}${key}.${index}.`,
                            true
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            </TableCell>
          </TableRow>
        );
      }

      return (
        <TableRow key={`${prefix}${key}`}>
          <TableCell
            sx={{
              pl: isNested ? 6 : 4,
              fontSize: "15px",
              border: "1px solid #E1E7EF",
              width: "35%",
              wordBreak: "break-word",
              fontWeight: 500,
            }}
          >
            {key}
          </TableCell>
          <TableCell
            sx={{
              fontSize: "15px",
              border: "1px solid #E1E7EF",
              width: "65%",
              wordBreak: "break-word",
              whiteSpace: "normal",
              textAlign: "left !important",
            }}
          >
            {renderValue(value)}
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        mb: 3,
        maxHeight: "60vh",
        overflow: "auto",
        border: "1px solid #dddddd",
        boxShadow: "none",
        "& .MuiTableCell-root": {
          padding: "12px 16px",
          maxWidth: "400px",
        },
      }}
    >
      <Table
        size="medium"
        stickyHeader
        sx={{
          border: "1px solid #e0e0e0",
          overflow: "hidden",
        }}
      >
        <TableHead>
          <TableRow sx={{ backgroundColor: "#f9fafb" }}>
            <TableCell
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                color: "#555",
                backgroundColor: "#f9fafb",
                width: "35%",
                border: "1px solid #E1E7EF",
              }}
            >
              Field
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                color: "#555",
                backgroundColor: "#f9fafb",
                width: "65%",
                border: "1px solid #E1E7EF",
              }}
            >
              Value
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderObjectRows(displayData)}</TableBody>
      </Table>
    </TableContainer>
  );
};

ResponseTable.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  index: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

const DEFAULT_RESPONSE = { image: null, text: null, status: null };

const getTransactionIdFromResponse = (response) => (
  response?.text?.tran_id ||
  response?.text?.transaction_id ||
  response?.text?.transactionId ||
  response?.text?.data?.tran_id ||
  response?.text?.data?.transactionId ||
  null
);

const buildUpdatedResponse = (textResponse) => {
  if (typeof textResponse === 'object' && textResponse !== null) {
    const detectedFilesCleaned = textResponse.detectedFiles
      ?.map((file) => file.replace(/\n/g, ''))
      .filter(Boolean);

    const requestId =
      textResponse?.requestId ||
      textResponse?.request_id ||
      textResponse?.data?.requestId ||
      textResponse?.data?.request_id;

    return {
      ...(requestId ? { requestId } : {}),
      ...textResponse,
      ...(detectedFilesCleaned?.length
        ? { detectedFiles: detectedFilesCleaned }
        : {}),
    };
  }

  if (typeof textResponse === 'string') {
    try {
      const parsed = JSON.parse(textResponse);
      const requestId =
        parsed?.requestId ||
        parsed?.request_id ||
        parsed?.data?.requestId ||
        parsed?.data?.request_id;

      return {
        ...(requestId ? { requestId } : {}),
        ...parsed,
        detectedFiles: parsed.detectedFiles?.map((file) =>
          file.replace(/\n/g, '')
        ),
      };
    } catch (parseError) {
      return {
        error: textResponse || 'Something went wrong. Please try again later.',
      };
    }
  }

  return {
    error: 'Something went wrong. Please try again later.',
  };
};

const renderJsonBlock = (data) => (
  <pre
    style={{
      whiteSpace: 'pre-wrap',
      background: 'rgb(245, 245, 245)',
      padding: '20px',
      minHeight: '40rem',
      overflowX: 'auto',
      fontSize: '15px',
      lineHeight: '1.6',
      borderRadius: '4px',
    }}
  >
    {JSON.stringify(data, null, 2)}
  </pre>
);

const getResponseContent = ({
  response,
  errorObject,
  isPdf,
  tabValue,
  handleTabChange,
  pageNumber,
  setPageNumber,
}) => {
  if (isPdf) {
    return (
      <PdfViewer
        open
        onClose={() => {}}
        isPdf={isPdf}
        previewImage={response?.image?.masked}
        setPageNumber={setPageNumber}
        pageNumber={pageNumber}
        useDialog={false}
      />
    );
  }

  if (response?.image?.masked) {
    return (
      <Box
        component="img"
        src={response.image.masked}
        sx={{ width: '100%', height: '100%' }}
      />
    );
  }

  if (response?.image?.error || errorObject) {
    const rawError = response?.image?.error || errorObject;
    const errorData = rawError?.message || rawError?.error || rawError;
    const parsedError =
      typeof errorData === 'string' ? { error: errorData } : errorData;

    return (
      <Box sx={{ px: 2, pt: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab
            label="Data Preview"
            sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}
          />
          <Tab label="JSON" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }} />
        </Tabs>
        <TabPanel value={tabValue} index={0}>
          <ResponseTable data={parsedError} />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {renderJsonBlock(rawError)}
        </TabPanel>
      </Box>
    );
  }

  if (response?.text) {
    const updatedResponse = buildUpdatedResponse(response.text);

    return (
      <Box sx={{ px: 2, pt: 3 }}>
        <Tabs
          value={tabValue}
          sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}
          onChange={handleTabChange}
        >
          <Tab
            label="Data Preview"
            sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}
          />
          <Tab label="JSON" sx={{ fontSize: '1.5rem', fontWeight: 'bold' }} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <ResponseTable data={updatedResponse} />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {renderJsonBlock(updatedResponse)}
        </TabPanel>
      </Box>
    );
  }

  return <Typography sx={{ p: 3, fontSize: '16px' }}></Typography>;
};

export function ApiResponse({
  loader = false,
  response = DEFAULT_RESPONSE,
  errorObject = null,
  hideStatus = false,
}) {
  const theme = useTheme();
  const [pageNumber, setPageNumber] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  const [transactionId, setTransactionId] = useState(null);

  const isPdf = response?.image?.masked?.match(/\.pdf$/i);
  useEffect(() => {
    const id = getTransactionIdFromResponse(response);
    if (id) {
      setTransactionId(id);
    }
  }, [response]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  const content = getResponseContent({
    response,
    errorObject,
    isPdf,
    tabValue,
    handleTabChange,
    pageNumber,
    setPageNumber,
  });
  const responseContextValue = useMemo(
    () => ({ transactionId, setTransactionId }),
    [transactionId]
  );

  const status = loader ? null : response?.status;
  const statusColor = status === 200 ? 'green' : 'red';

  return (
    <ResponseContext.Provider value={responseContextValue}>
      <Stack>
        {!hideStatus && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ mt: 2, mb: 2, color: theme.palette.textColor.primary }}>
              Status:
            </Typography>
            <Typography variant="h5" sx={{ color: statusColor, px: 1 }}>
              {loader ? <Loader /> : status ?? ''}
            </Typography>
          </Box>
        )}
        <Box sx={{ 
          background: theme.palette.background.main, 
          width: '100%', 
          height: '100%', 
          minHeight: '40rem' 
        }}>
          {content}
        </Box>
      </Stack>
    </ResponseContext.Provider>
  );
}

ApiResponse.propTypes = {
  loader: PropTypes.bool,
  response: PropTypes.shape({
    image: PropTypes.shape({
      masked: PropTypes.string,
      error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    }),
    text: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    status: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  errorObject: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  hideStatus: PropTypes.bool,
};

export const useResponseContext = () => useContext(ResponseContext);
export default ApiResponse;